<template>
  <WdsAlert v-if="validationErrors && validationErrors.length" error class="tw-mb-2">
    <ul class="tw-mb-0">
      <li v-for="validationError in validationErrors" :key="validationError">
        {{ validationError }}
      </li>
    </ul>
  </WdsAlert>
  <div class="tw-h-[60vh] tw-overflow-auto tw-flex tw-flex-col">
    <CandidatesGraphqlTable
      multi-select
      max-height-adjustment="30vh"
      @rows-selected="handleRowsSelected"
    >
      <template #no-results>
        <NoTrustedContactsFound />
      </template>
    </CandidatesGraphqlTable>
  </div>
  <div
    class="-tw-mx-6 select-talent-footer tw-flex tw-items-center tw-justify-between tw-pt-4 tw-pb-5 tw-px-6"
  >
    <WdsButton @click="$emit('cancel')">
      {{ $t('Cancel') }}
    </WdsButton>

    <div class="tw-flex">
      <WdsAvatarGroup sm class="tw-mr-6" :max-avatars="3" :avatars="selectedCandidates" />

      <WdsButton
        primary
        :disabled="selectedCandidates.length === 0 || createJobShareLoading"
        class="tw-flex tw-flex-row tw-gap-2 tw-items-center"
        data-test="add-candidates"
        @click="addCandidates"
      >
        <WdsSpinner v-if="createJobShareLoading" indeterminate />

        {{ $t('Add candidates') }}
      </WdsButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Ref, computed, provide, ref } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { WdsButton, WdsAvatarGroup, WdsSpinner, WdsAlert } from '@worksome/wds-components'

import { $t } from '@/composables/translation'
import useApolloError from '@/composables/useApolloError'
import NetworkError from '@/utils/notification/NetworkError'
import CandidatesGraphqlTable from '@/components/Contacts/CandidatesGraphqlTable.vue'
import NoTrustedContactsFound from '@/components/Contacts/CompanyFreelancer/NoTrustedContactsFound.vue'

import { formatTrustedContactAsAvatar } from '@/components/Job/functions'
import { TrustedContact } from '@/components/Job/hiring-types'
import { CreateJobShareMutation } from '@/components/Job/Share/graphql/mutations'
import { TableInModal } from '@/components/GraphqlDataTable/injection'

const props = defineProps<{
  companyId: string
  jobId: string
  preSelectedCandidates: string[]
}>()

const emit = defineEmits(['candidates-updated', 'cancel'])

provide(TableInModal, true)

const selectedCandidates = ref<{ id: string; path: string }[]>([])

const {
  loading: createJobShareLoading,
  mutate: mutateCreateJobShare,
  error: errorCreateJobShare,
} = useMutation(CreateJobShareMutation, {
  clientId: 'publicApi',
})
const { validationErrors: validationErrorsCreateJobShare }: { validationErrors: Ref<string[]> } =
  useApolloError(errorCreateJobShare)

const validationErrors = computed(() => Object.values(validationErrorsCreateJobShare.value))

function normalizeRowToTrustedContact(row: any): TrustedContact | null {
  if (!row?.id || !row?.worker?.id) return null
  return {
    id: row.id,
    workerId: row.worker.id,
    avatar: row.worker.avatar ?? '',
    name: row.worker.name ?? '',
    title: row.worker.jobTitle ?? '',
  }
}

const handleRowsSelected = (rows: any[]) => {
  selectedCandidates.value = rows
    .map(normalizeRowToTrustedContact)
    .filter((t): t is TrustedContact => t !== null)
    .filter((contact) => !props.preSelectedCandidates.includes(contact.workerId))
    .map(formatTrustedContactAsAvatar)
}

const addCandidates = async () => {
  try {
    await mutateCreateJobShare({
      input: {
        job: props.jobId,
        accounts: selectedCandidates.value.map((contact) => contact.id),
      },
    })

    emit('candidates-updated')
    emit('cancel')
  } catch (ex: any) {
    new NetworkError({
      ex,
      msg: $t('An error occurred while saving the candidates'),
    }).dispatch()
  }
}
</script>

<style lang="scss" scoped>
@import 'resources/sass/frontend/_color-variables';

.select-talent-footer {
  border-top: 1px solid $gray-300;
}
</style>
